/// <amd-module name="Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsSelector" />
import * as Reselect from "Reselect";
import { getAllColumnValues, getWITColumnFields } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/ExecuteColumnOptionUtils";
import {
    IColumnAndSortOrder,
    IOutcome,
    IPointsAwareState,
    ITestCaseReference,
    ITestPointConfiguration,
    ITestPointItem
} from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import * as Resources from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Resources";
import { getTestPointItemsComparator } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/TestPointsSortingUtility";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { IUserIdentity } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import { getIdentityDisplayString, getExecuteColumnOptionFields } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import * as TestHubStringResources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { ITestHubListBoxItemData } from "Test/TestPlansHubRefresh/TestPlansHubRefreshFilter/TestPlansHubRefreshFilterComponent.Types";
import { Outcome, PointState, TestCase, TestPoint } from "TestPlanCommon/Contracts/TestPlan";
import { ObservableArray } from "VSS/Core/Observable";
import { IIconProps } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { IFilterState } from "VSSUI/Utilities/Filter";

export const getTestPointsMapFromState = (state: IPointsAwareState): { [id: number]: TestPoint } => state.executeState.testPoints;

export const getColumnAndSortOrder = (state: IPointsAwareState): IColumnAndSortOrder | undefined => {
    if (state && state.executeState) {
        return state.executeState.columnAndSortOrder;
    }
};

export const isAnyExecuteFilterValueSet = (filterState: IFilterState | undefined) => {
    let isFilterValueSet = false;

    const executeFilterItemKeys = [
        TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY,
        TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY,
        TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY,
        TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY,
        TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY,
        TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY
    ];

    // check if any of the execute filter item keys are set
    if (filterState) {
        for (const key of executeFilterItemKeys) {
            const filterItemState = filterState[key];
            if (filterItemState && filterItemState.value && filterItemState.value.length) {
                isFilterValueSet = true;
                break;
            }
        }
    }

    return isFilterValueSet;
};

export const getFilteredTestPoints = (state: IPointsAwareState): number[] | undefined => {
    if (state && state.executeFilterState) {
        return state.executeFilterState.filteredTestPointIds;
    }
};

export const getWITColumnOptionFields = (state: IPointsAwareState): string | undefined => {
    if (state && state.testHubRefreshState && state.testHubRefreshState.executeColumnOptionFields) {
        return getWITColumnFields(state.testHubRefreshState.executeColumnOptionFields).join(",");
    }
};

export const getWITColumnOptionFieldsArray = (state: IPointsAwareState): string[] | undefined => {
    let witFields: string[] = [];
    if (state && state.testHubRefreshState && state.testHubRefreshState.executeColumnOptionFields) {
        witFields = getWITColumnFields(state.testHubRefreshState.executeColumnOptionFields);
    }
    return witFields;
};

export const getTestCaseItemsFromState = (state: IPointsAwareState): { [id: number]: TestCase } | undefined => {
    if (state.executeState && state.executeState.testCases) {
        return state.executeState.testCases;
    }

    return undefined;
};

export const hasColumnOptionsLoadStarted = (state: IPointsAwareState): boolean => {
    if (state && state.executeState && state.executeState.testCases) {
        return true;
    }
    return false;
};

export const getTestPointsCount = (state: IPointsAwareState): number | undefined => {
    if (state && state.executeState && state.executeState.testPoints) {
        return Object.keys(state.executeState.testPoints).length;
    }

    return undefined;
};

export const getTestPointsFromState = (state: IPointsAwareState): TestPoint[] => {
    const allKeys: number[] = Object.keys(state.executeState.testPoints).map(key => parseInt(key));
    const testPoints: TestPoint[] = allKeys.map(key => state.executeState.testPoints[key]);

    return testPoints;
};

export const getTestPointsFromTestPointItems = (state: IPointsAwareState, testPointItems: ITestPointItem[]): TestPoint[] => {
    const testPoints: TestPoint[] = [];
    if (testPointItems && testPointItems.length > 0) {
        const testPointMap: { [id: number]: TestPoint } = getTestPointsMapFromState(state);
        for (const testPointItem of testPointItems) {
            if (testPointItem && testPointItem.id != null) {
                testPoints.push(testPointMap[testPointItem.id]);
            }
        }
    }

    return testPoints;
};

export const getTestPointItemsFromState = (state: IPointsAwareState, fetchTags?: boolean, testCases?: TestCase[] | undefined): ITestPointItem[] | undefined => {
    if (state.executeState && state.executeState.testPoints) {
        const testPoints: TestPoint[] = getTestPointsFromState(state);
        return getTestPointItemsFromTestPoint(testPoints, fetchTags, testCases);
    }

    return undefined;
};

export const getTestPointItemsFromTestPoint = (testPoints: TestPoint[], fetchTags?: boolean, testCases?: TestCase[] | undefined): ITestPointItem[] => {
    const testPointItems: ITestPointItem[] = [];

    if (testPoints) {
        for (let i = 0; i < testPoints.length; i++) {
            testPointItems.push({
                id: _getTestPointId(testPoints[i]),
                outcomeItem: _getTestPointOutcome(testPoints[i]),
                tester: _getTestPointTester(testPoints[i]),
                configuration: _getTestPointConfiguration(testPoints[i]),
                state: _getTestPointState(testPoints[i]),
                testCaseRef: _getTestCaseRef(testPoints[i]),
                lastResultId: testPoints[i].results ? testPoints[i].results.lastResultId : 0,
                lastRunId: testPoints[i].results ? testPoints[i].results.lastTestRunId : 0,
                isAutomated: testPoints[i].isAutomated ? testPoints[i].isAutomated : false
            });
            if (fetchTags && testCases) {
                testPointItems[i].columnOptionValues = getAllColumnValues([TestHubRefreshConstants.WorkItemField_Tags], testCases[testPoints[i].id], testPoints[i]);
            }
        }
    }

    return testPointItems;
};

export const getTestPointsToRender = Reselect.createSelector(
    getTestPointsMapFromState,
    getTestPointItemsFromState,
    getTestCaseItemsFromState,
    getExecuteColumnOptionFields,
    getFilteredTestPoints,
    getColumnAndSortOrder,
    (
        allTestPointsMap: { [id: number]: TestPoint },
        allTestPointItems: ITestPointItem[] | undefined,
        allTestCaseItems: { [id: number]: TestCase } | undefined,
        allColumnOptionFields: any[],
        filteredTestPoints: number[] | undefined,
        columnAndSortOrder: IColumnAndSortOrder
    ): ITestPointItem[] | undefined => {
        // return allTestPointItems;
        const arrayToReturn: ITestPointItem[] = [];
        const tempFilteredTestPoints: TestPoint[] = [];
        let tempFilteredTestPointsItems: ITestPointItem[] = [];

        // if filteredTestPoints is not valid, return all the test point items to render
        if (!filteredTestPoints) {
            if (allTestPointItems) {
                if (allTestPointsMap && allTestCaseItems && allColumnOptionFields) {
                    for (const point of allTestPointItems) {
                        const pointId = point.id;
                        point.columnOptionValues = getAllColumnValues(allColumnOptionFields, allTestCaseItems[pointId], allTestPointsMap[pointId]);
                        if (allTestCaseItems[pointId] && allTestCaseItems[pointId].order) {
                            point.order = allTestCaseItems[pointId].order + 1;
                        } else {
                            // For the test point with order 0, the property comes in as undefined.
                            point.order = 1;
                        }
                    }
                }

                allTestPointItems = allTestPointItems.sort(getTestPointItemsComparator(columnAndSortOrder.columnId, columnAndSortOrder.sortOrder));
            }
            return allTestPointItems;
        }

        // this means filter is active, but no items match the filter. Return empty items in this case so we can render empty table
        if (filteredTestPoints && filteredTestPoints.length === 0) {
            return arrayToReturn;
        }

        for (let i = 0; i < filteredTestPoints.length; i++) {
            if (allTestPointsMap[filteredTestPoints[i]]) {
                tempFilteredTestPoints.push(allTestPointsMap[filteredTestPoints[i]]);
            }
        }

        if (tempFilteredTestPoints) {
            tempFilteredTestPointsItems = getTestPointItemsFromTestPoint(tempFilteredTestPoints);

            if (allTestPointsMap && allTestCaseItems && allColumnOptionFields) {
                for (const filteredPoint of tempFilteredTestPointsItems) {
                    const pointId = filteredPoint.id;
                    filteredPoint.columnOptionValues = getAllColumnValues(
                        allColumnOptionFields,
                        allTestCaseItems[pointId],
                        allTestPointsMap[pointId]
                    );
                    if (allTestCaseItems[pointId] && allTestCaseItems[pointId].order) {
                        filteredPoint.order = allTestCaseItems[pointId].order + 1;
                    } else {
                        filteredPoint.order = 1;
                    }
                }
            }

            if (tempFilteredTestPointsItems) {
                tempFilteredTestPointsItems = tempFilteredTestPointsItems.sort(
                    getTestPointItemsComparator(columnAndSortOrder.columnId, columnAndSortOrder.sortOrder)
                );
            }
        }
        return tempFilteredTestPointsItems;
    }
);

/**
 * getUniqueFilterValuesToRender: This selector loops through all the items and returns an object containing unique values for each of the Execute filter (outcome, tester, configuration etc.)
 */
export const getUniqueFilterValuesToRender = Reselect.createSelector(getTestPointItemsFromState, (allTestPointItems: ITestPointItem[]) => {
    const outcomeValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> = new ObservableArray<IListBoxItem<ITestHubListBoxItemData>>();
    const testerValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> = new ObservableArray<IListBoxItem<ITestHubListBoxItemData>>();
    const configurationValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> = new ObservableArray<IListBoxItem<ITestHubListBoxItemData>>();
    const assignedToValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> = new ObservableArray<IListBoxItem<ITestHubListBoxItemData>>();
    const stateValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> = new ObservableArray<IListBoxItem<ITestHubListBoxItemData>>();

    if (allTestPointItems && allTestPointItems.length > 0) {
        const outcomeMap: Map<string, number> = new Map<string, number>();
        const testerMap: Map<string, number> = new Map<string, number>();
        const configurationMap: Map<string, number> = new Map<string, number>();
        const assignedToMap: Map<string, number> = new Map<string, number>();
        const stateMap: Map<string, number> = new Map<string, number>();

        for (const testPointItem of allTestPointItems) {
            // process outcome field
            if (!outcomeMap.has(testPointItem.outcomeItem.outcomeString)) {
                outcomeMap.set(testPointItem.outcomeItem.outcomeString, testPointItem.id);

                const item: IListBoxItem<ITestHubListBoxItemData> = {
                    id: testPointItem.outcomeItem.outcomeString,
                    text: testPointItem.outcomeItem.outcomeString,
                    data: {
                        key: testPointItem.outcomeItem.outcomeString,
                        value: testPointItem.outcomeItem
                    },
                    iconProps: _getTesterFilterItemIconProps(testPointItem.outcomeItem)
                };
                outcomeValues.push(item);
            }

            // process tester field
            if (testPointItem.tester) {
                if (!testerMap.has(testPointItem.tester.id.toLowerCase())) {
                    testerMap.set(testPointItem.tester.id.toLowerCase(), testPointItem.id);

                    const item: IListBoxItem<ITestHubListBoxItemData> = {
                        id: testPointItem.tester.displayName,
                        text: testPointItem.tester.displayName,
                        data: {
                            key: testPointItem.tester.displayName,
                            value: testPointItem.tester
                        }
                    };
                    testerValues.push(item);
                }
            }
            // process configuration field
            if (testPointItem.configuration) {
                if(testPointItem.configuration.name){
                    if (!configurationMap.has(testPointItem.configuration.name)) {
                        configurationMap.set(testPointItem.configuration.name, testPointItem.id);

                        const item: IListBoxItem<ITestHubListBoxItemData> = {
                            id: testPointItem.configuration.name,
                            text: testPointItem.configuration.name,
                            data: {
                                key: testPointItem.configuration.name,
                                value: testPointItem.configuration
                            }
                        };
                        configurationValues.push(item);
                    }
                }
            }
            // process assignedTo field
            if (!assignedToMap.has(testPointItem.testCaseRef.assignedTo.id.toLowerCase())) {
                assignedToMap.set(testPointItem.testCaseRef.assignedTo.id.toLowerCase(), testPointItem.id);

                const item: IListBoxItem<ITestHubListBoxItemData> = {
                    id: testPointItem.testCaseRef.assignedTo.displayName,
                    text: testPointItem.testCaseRef.assignedTo.displayName,
                    data: {
                        key: testPointItem.testCaseRef.assignedTo.displayName,
                        value: testPointItem.testCaseRef.assignedTo
                    }
                };
                assignedToValues.push(item);
            }

            // process state field
            if (!stateMap.has(testPointItem.testCaseRef.state)) {
                stateMap.set(testPointItem.testCaseRef.state, testPointItem.id);

                const item: IListBoxItem<ITestHubListBoxItemData> = {
                    id: testPointItem.testCaseRef.state,
                    text: testPointItem.testCaseRef.state,
                    data: {
                        key: testPointItem.testCaseRef.state,
                        value: testPointItem.testCaseRef.state
                    }
                };
                stateValues.push(item);
            }
        }
    }

    const wrapperObject: { [key: string]: ObservableArray<IListBoxItem<ITestHubListBoxItemData>> } = {};
    wrapperObject[TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY] = outcomeValues;
    wrapperObject[TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY] = testerValues;
    wrapperObject[TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY] = configurationValues;
    wrapperObject[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY] = assignedToValues;
    wrapperObject[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY] = stateValues;

    return wrapperObject;
});

export const _getTestPointTester = (testPoint: TestPoint): IUserIdentity => {
    let tester: IUserIdentity = {
        displayName: TestHubStringResources.UnassignedString,
        id: TestHubRefreshConstants.EmptyGuid
    };

    if (testPoint && testPoint.tester && testPoint.tester.displayName) {
        const displayName = getIdentityDisplayString(testPoint.tester.displayName);
        tester = {
            displayName: displayName,
            id: testPoint.tester.id
        };
    }

    return tester;
};

const _getTestPointId = (testPoint: TestPoint): number => testPoint.id;

export const _getTesterFilterItemIconProps = (outcomeItem: IOutcome): IIconProps => {
    let iconName = "CircleFill";
    let className = "active-icon";
    const iconProps: IIconProps = {
        iconName: iconName,
        className: className
    };

    switch (outcomeItem.outcome) {
        case Outcome.Passed:
            iconName = "CompletedSolid";
            className = "pass-icon";
            break;

        case Outcome.Failed:
            iconName = "StatusErrorFull";
            className = "fail-icon";
            break;

        case Outcome.Blocked:
            iconName = "Blocked2Solid";
            className = "block-icon";
            break;

        case Outcome.NotApplicable:
            iconName = "BlockedSolid";
            className = "not-applicable-icon";
            break;

        case Outcome.Paused:
            iconName = "CirclePauseSolid";
            className = "active-icon";
            break;

        case Outcome.InProgress:
            iconName = "AwayStatus";
            className = "active-icon";
            break;

        default:
            iconName = "CircleFill";
            className = "active-icon";
    }

    iconProps.iconName = iconName;
    iconProps.className = className;
    return iconProps;
};

const _getTestPointOutcome = (testPoint: TestPoint): IOutcome => {
    const pointOutcome = testPoint.results.outcome;
    const state = _getTestPointState(testPoint);
    let outcomeItem: IOutcome = {} as IOutcome;
    if (state === PointState.InProgress) {
        if (pointOutcome === Outcome.Paused) {
            outcomeItem = {
                outcome: testPoint.results.outcome,
                outcomeString: Resources.Paused
            };
        }
        else {
            outcomeItem = {
                outcome: Outcome.InProgress,
                outcomeString: Resources.InProgress
            };
        }
    }
    else if (pointOutcome === Outcome.Passed) {
        outcomeItem = {
            outcome: testPoint.results.outcome,
            outcomeString: Resources.Passed
        };
    } else if (pointOutcome === Outcome.Failed) {
        outcomeItem = {
            outcome: testPoint.results.outcome,
            outcomeString: Resources.Failed
        };
    } else if (pointOutcome === Outcome.Blocked) {
        outcomeItem = {
            outcome: testPoint.results.outcome,
            outcomeString: Resources.Blocked
        };
    } else if (pointOutcome === Outcome.NotApplicable) {
        outcomeItem = {
            outcome: testPoint.results.outcome,
            outcomeString: Resources.NotApplicable
        };
    } else if (pointOutcome === Outcome.Paused) {
        outcomeItem = {
            outcome: testPoint.results.outcome,
            outcomeString: Resources.Paused
        };
    } else if (pointOutcome === Outcome.Inconclusive) {
        outcomeItem = {
            outcome: Outcome.Inconclusive,
            outcomeString: Resources.Inconclusive
        };
    } else if (pointOutcome === Outcome.NotExecuted) {
        outcomeItem = {
            outcome: Outcome.NotExecuted,
            outcomeString: Resources.NotExecuted
        };
    } else if (pointOutcome === Outcome.NotImpacted) {
        outcomeItem = {
            outcome: Outcome.NotImpacted,
            outcomeString: Resources.NotImpacted
        };
    } else if (pointOutcome === Outcome.Timeout) {
        outcomeItem = {
            outcome: Outcome.Timeout,
            outcomeString: Resources.Timeout
        };
    } else if (pointOutcome === Outcome.Warning) {
        outcomeItem = {
            outcome: Outcome.Warning,
            outcomeString: Resources.Warning
        };
    } else if (pointOutcome === Outcome.Error) {
        outcomeItem = {
            outcome: Outcome.Error,
            outcomeString: Resources.Error
        };
    } else if (pointOutcome === Outcome.InProgress) {
        outcomeItem = {
            outcome: testPoint.results.outcome,
            outcomeString: Resources.InProgress
        };
    }
    // For unspecified and None outcome, we will show active
    else {
        outcomeItem = {
            outcome: testPoint.results.outcome,
            outcomeString: Resources.Active
        };
    }
    return outcomeItem;
};

const _getTestPointState = (testPoint: TestPoint): PointState => {
    return testPoint.results.state;
};

const _getTestPointConfiguration = (testPoint: TestPoint): ITestPointConfiguration => {
    const pointConfig: ITestPointConfiguration = {
        id: -1,
        name: ""
    };

    if (testPoint && testPoint.configuration) {
        pointConfig.id = testPoint.configuration.id,
            pointConfig.name = testPoint.configuration.name
    }

    return pointConfig;
};

const _getTestCaseRef = (testPoint: TestPoint): ITestCaseReference => {
    const testCaseRef: ITestCaseReference = {
        id: -1,
        assignedTo: {
            displayName: TestHubStringResources.UnassignedString,
            id: TestHubRefreshConstants.EmptyGuid
        },
        state: "",
        title: ""
    };

    if (testPoint && testPoint.testCaseReference) {
        if (testPoint.testCaseReference.assignedTo) {
            const assignedTo = testPoint.testCaseReference.assignedTo;

            testCaseRef.assignedTo.displayName = assignedTo.displayName ? assignedTo.displayName : assignedTo.uniqueName;
            testCaseRef.assignedTo.id = assignedTo.id ? assignedTo.id : TestHubRefreshConstants.EmptyGuid;
        }
        testCaseRef.id = testPoint.testCaseReference.id;
        testCaseRef.state = testPoint.testCaseReference.state;
        testCaseRef.title = testPoint.testCaseReference.name;
    }

    return testCaseRef;
};
